import { useMediaQuery, Box, Typography, Divider, Grid, TextField, useTheme, Tab, Tabs, Button } from '@mui/material';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Frequenters, Participants, ToolsDetails } from '../../../../../../components/custom';
import { CustomAlert } from '../../../../../../components/shared';
import { LayoutBase } from '../../../../../../layouts';
import { IFrequentadores, ISeminario, SeminarioServices } from '../../../../../../services/api/action-plan/Seminario';
import { IParticipantes } from '../../../../../../services/api/action-plan/InternalTraining';
import React from 'react';
import { useAuthContext } from '../../../../../../context';
import { NavigateBefore, NavigateNext, Save } from '@mui/icons-material';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography component='div'>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function tabProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}


export const AnnualGuidanceSeminarDetails = () => {

    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [value, setValue] = React.useState(0);
    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };
    const { userData } = useAuthContext();
    const url = useParams();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const [valid, setValid] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [data, setData] = useState<ISeminario>({
        participantes: [],
        frequentadores: [],
        id_barragem: '',
        periodo_referencia: '',
        data_seminario: '',
        responsavel_tecnico: '',
        art: '',
        instituicoes_participantes: '',
    });

    const pagePermissionsString = useMemo(() => {
        return userData?.nivel_acesso?.PAE?.seminario;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    useEffect(() => {
        setData({
            participantes: [],
            frequentadores: [],
            id_barragem: '',
            periodo_referencia: '',
            data_seminario: '',
            responsavel_tecnico: '',
            art: '',
            instituicoes_participantes: '',
        });
        fetchData();
    }, [location.pathname]);

    function verifyForm() {
        return !!data.data_seminario && !!data.responsavel_tecnico;
    }

    const handleNew = () => {
        if (!hasActionPermission('add')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para adicionar um novo seminário.', severity: 'error' });
            return;
        }
        navigate(`/information_dam/${url.id}/annual_guidance_seminar/detalhes/novo`);
    };

    const fetchData = () => {
        if (url.idSeminar) {
            SeminarioServices.get(url.idSeminar)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                    } else {
                        response.data_seminario = response.data_seminario ? new Date(response.data_seminario).toISOString().substring(0, 10) : '';
                        response.periodo_referencia = response.periodo_referencia ? new Date(response.periodo_referencia).toISOString().substring(0, 10) : '';
                        setData(response);
                    }
                });
        }
    };

    const handleParticipants = useCallback((value: IParticipantes[]) => {
        setData(prevData => ({
            ...prevData,
            participantes: value
        }));
    }, []);

    const handleFrequenters = useCallback((value: IFrequentadores[]) => {
        setData(prevData => ({
            ...prevData,
            frequentadores: value
        }));
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const newData = {
            ...data,
            [name]: value,
        };
        setData(newData);
        setValid(false);
    };

    const handleSave = () => {
        const isEditing = url.idSeminar !== 'novo';
        const hasAddPermission = hasActionPermission('add');
        const hasEditPermission = hasActionPermission('edit');
        if ((!isEditing && !hasAddPermission) || (isEditing && !hasEditPermission)) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para realizar esta ação.', severity: 'error' });
            return;
        }

        if (url.idSeminar == 'novo') {
            if (verifyForm()) {
                if (url.id) {
                    data.id_barragem = url.id;
                    SeminarioServices.create(data)
                        .then((response) => {
                            if (response instanceof Error) {
                                setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });
                            } else {
                                setAlertInfo({ open: true, message: 'Seminario Criado', severity: 'success' });
                                setTimeout(() => {
                                    navigate(`/information_dam/${url.id}/annual_guidance_seminar`);
                                }, 2000);
                            }
                        });
                }
            } else {
                setValid(true);
            }
        } else {
            if (url.idSeminar !== undefined) {
                delete data.uuid;
                SeminarioServices.upgrade(data, url.idSeminar)
                    .then((response) => {
                        if (response instanceof Error) {
                            setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });
                        } else {
                            setAlertInfo({ open: true, message: 'Seminario atualizado', severity: 'success' });
                            setTimeout(() => {
                                navigate(`/information_dam/${url.id}/annual_guidance_seminar`);
                            }, 2000);
                        }
                    });
            }
        }
    };

    const handleDelete = () => {
        if (!hasActionPermission('remove')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para deletar este seminário.', severity: 'error' });
            return;
        }
        if (url.idAco !== undefined && url.idAco !== 'novo') {
            SeminarioServices.delet(url.idAco)
                .then((response) => {
                    if (response instanceof Error) {
                        // console.log(response.message);
                        setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });

                    } else {
                        setAlertInfo({ open: true, message: 'ACO excluido', severity: 'success' });
                    }
                });
        }
    };

    const handleChangeButtonNext = () => {
        if (value < 2) {
            setValue(value + 1);
        }
    };
    const handleChangeButtonBefor = () => {
        if (value > 0) {
            setValue(value - 1);
        }
    };

    return (
        <LayoutBase
            title='Cadastro Seminário'
            tollBar={<ToolsDetails
                showDeletButton={url.idSeminar !== 'novo' && hasActionPermission('remove')}
                showSaveButton={url.idSeminar == 'novo' ? hasActionPermission('add') : hasActionPermission('edit')}
                showNewButton={hasActionPermission('add')}
                onClickSave={() => handleSave()}
                onClickNew={() => handleNew()}
                onClickDelet={() => handleDelete()}
            />}
        >
            <Box padding={1}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} justifyContent={'space-around'} display={'flex'}>
                    <Tabs value={value} onChange={handleChangeTab} variant="scrollable" scrollButtons allowScrollButtonsMobile >
                        <Tab sx={{ color: valid ? '#d95b40' : theme.palette.primary.main, '&.Mui-disabled': { color: valid ? '#d95b40' : theme.palette.primary.main } }} label="Informações seminario" {...tabProps(0)} disabled />
                        <Tab sx={{ color: theme.palette.primary.main, '&.Mui-disabled': { color: theme.palette.primary.main } }} label="Participantes" {...tabProps(1)} disabled />
                        <Tab sx={{ color: theme.palette.primary.main, '&.Mui-disabled': { color: theme.palette.primary.main } }} label="Frequentadores/Moradores" {...tabProps(2)} disabled />
                    </Tabs>
                </Box>
                <Box padding={1} display={'flex'} justifyContent={'space-between'} flexDirection={smDown ? 'column' : 'row'} gap={smDown ? '5px' : 0}>
                    <Button
                        variant='contained'
                        sx={{ borderRadius: '25px' }}
                        startIcon={<NavigateBefore />}
                        disabled={value == 0}
                        onClick={() => handleChangeButtonBefor()}
                    >Voltar</Button>
                    <Button
                        variant='contained'
                        sx={{ borderRadius: '25px' }}
                        startIcon={value != 2 ? <NavigateNext /> : <Save />}
                        onClick={value != 2 ? () => handleChangeButtonNext() : () => handleSave()}
                        disabled={
                            (url.idSeminar == 'novo' && !hasActionPermission('add')) ||
                            (url.idSeminar !== 'novo' && !hasActionPermission('edit'))
                        }
                    >{value != 2 ? 'Proximo' : 'Salvar'}</Button>

                </Box>
                <CustomTabPanel value={value} index={0}>
                    <Box>
                        <Box
                            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'start' }}
                            padding={1}
                        >
                            <Typography><strong>Informações seminario orientaivo</strong></Typography>
                        </Box>
                        <Divider />
                        <Grid container padding={2} spacing={2}>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Data seminário'
                                    type='date'
                                    name='data_seminario'
                                    fullWidth
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.data_seminario}
                                    error={valid ? (data.data_seminario == '' ? true : false) : false}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Ano/Período de referência '
                                    type='date'
                                    name='periodo_referencia'
                                    fullWidth
                                    required
                                    onChange={(e) => handleChange(e)}
                                    value={data?.periodo_referencia}
                                    InputLabelProps={{ shrink: true }}
                                    error={valid ? (data.periodo_referencia == '' ? true : false) : false}

                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Responsável técnico'
                                    type='text'
                                    name='responsavel_tecnico'
                                    fullWidth
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.responsavel_tecnico}
                                    error={valid ? (data.responsavel_tecnico == '' ? true : false) : false}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='ART'
                                    type='text'
                                    name='art'
                                    fullWidth
                                    onChange={(e) => handleChange(e)}
                                    value={data?.art}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Instituições participantes'
                                    type='text'
                                    name='instituicoes_participantes'
                                    fullWidth
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.instituicoes_participantes}
                                    error={valid ? (data.instituicoes_participantes == '' ? true : false) : false}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </CustomTabPanel>

                <CustomTabPanel value={value} index={1}>
                    <Participants value={data.participantes} onChange={handleParticipants} smDown={smDown} />
                </CustomTabPanel>

                <CustomTabPanel value={value} index={2}>
                    <Frequenters value={data.frequentadores} onChange={handleFrequenters} smDown={smDown} />
                </CustomTabPanel>
            </Box>
            {alertInfo.open &&
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
        </LayoutBase>
    );
};